import type { CegoXmpp } from '@cego/xmpp'
import { createSharedComposable, shallowRef, until, useCoreLogger, useXmppStatus, useXmppConfig, useUsername } from '#imports'
import { useRuntimeConfig } from '#app'

export const useXmpp = createSharedComposable(() => {
    if (import.meta.server) {
        return {
            ensureInit: async () => undefined,
            getInstance: () => undefined,
        }
    }

    const config = useRuntimeConfig()
    const logger = useCoreLogger()
    const { setStatus } = useXmppStatus()

    const instance = shallowRef<InstanceType<typeof CegoXmpp>>()
    let initPromise: Promise<InstanceType<typeof CegoXmpp> | undefined> | null = null

    function ensureInit(): Promise<InstanceType<typeof CegoXmpp> | undefined> {
        if (initPromise) return initPromise

        initPromise = _init() ?? Promise.resolve(undefined)
        return initPromise
    }

    function _init(): Promise<InstanceType<typeof CegoXmpp>> | undefined {
        if (!config.public.xmpp.service || !config.public.xmpp.domain) {
            return undefined
        }

        return Promise.all([
            import('@cego/xmpp'),
            useUsername(),
            useXmppConfig({ immediate: false }),
        ]).then(async ([{ CegoXmpp }, { username, isLoading }, xmppConfig]) => {
            // The first room presence is sent when the socket comes online; joining before the
            // nameservice answers would use the jid. A disabled, failed or 401 query is not
            // loading, so this never waits on those. A stalled one must not keep chat and
            // wallet pushes down, so after the timeout this joins with the jid.
            await until(isLoading).toBe(false, { timeout: 5_000 })

            function getWebsocketNickname() {
                return username.value || xmppConfig.data.value?.jid
            }

            const xmpp = new CegoXmpp({
                service: config.public.xmpp.service,
                domain: config.public.xmpp.domain,
                maxListeners: 30,
                async credentials(auth: ({ username, password }: { username: string, password: string }) => void): Promise<void> {
                    await xmppConfig.refresh()

                    // The refresh can fail (offline reconnect, 403, 5xx) and leave no config.
                    // @xmpp/sasl restarts the stream whenever this resolves, auth or not, which
                    // ejabberd logs as "Unexpected info"; rejecting skips that restart.
                    const credentials = xmppConfig.data.value
                    if (!credentials) {
                        throw new Error('[xmpp] No credentials available after config refresh')
                    }

                    return auth({ username: credentials.jid, password: credentials.password })
                },
                async onlineHandler() {
                    // The socket can be torn down or re-enter CONNECTING between the
                    // 'online' event firing and these awaited sends resolving (e.g. a
                    // rapid reconnect, or tab backgrounding). onlineHandler is invoked
                    // by @cego/xmpp without a .catch, so a throw here surfaces as an
                    // unhandled rejection (null socket / "Still in CONNECTING state").
                    // Treat a transient presence failure as non-fatal.
                    try {
                        await xmpp.sendRoomPresence(config.public.core.site.brand, getWebsocketNickname())
                        await xmpp.sendRoomPresence('globalroom', getWebsocketNickname())
                    }
                    catch (err) {
                        logger.warn('[xmpp] Failed to send room presence on online', err)
                    }

                    logger.info('[xmpp] Online detected')
                    setStatus('online')
                },
                offlineHandler() {
                    logger.info('[xmpp] Offline detected')
                    setStatus('offline')
                },
                errorHandler(err) {
                    logger.error(err)
                },
            })

            xmpp.start()

            xmpp.on({ kind: 'message', type: 'error' }, ({ error }) => {
                console.error(error)
            })

            instance.value = xmpp

            return xmpp
        })
    }

    function getInstance(): InstanceType<typeof CegoXmpp> | undefined {
        return instance.value
    }

    return {
        ensureInit,
        getInstance,
    }
})
